import { ConfirmModal, PrimaryButton } from '@/components';
import { SidebarContent, SidebarFooter } from '@/components/layout/sidepanel/SidebarFooter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { addDocumentFileDeviceAccess, deleteDocumentFile, moveDocumentFile, removeDocumentFileDeviceAccess } from '../../api';
import { useDocumentFile, useInvalidateDocumentFileAll } from '../../hooks';
import { useDocumentDeviceState } from '../../hooks/useDocumentDeviceState';
import { useDocumentMetadataState } from '../../hooks/useDocumentMetadataState';
import { FileDetail } from '../../types';
import { getDocumentMutationErrorMessage } from '../../utils/documentErrors';
import { FolderPickerDialog } from '../FolderPickerDialog';
import { DocumentDeviceAssignmentSection, DocumentDeviceAssignmentState } from './DocumentDeviceAssignmentSection';
import { DocumentShareSection } from './DocumentShareSection';
import { createFileObjectsSectionActions } from './DocumentFileObjectsSection';
import { DocumentMetadataSection, DocumentMetadataState } from './DocumentMetadataSection';
import { SingleFileObject } from './SingleFileObject';
import { InitialFileObjectRow } from './types';
import { useFileObjectRows } from './useFileObjectRows';


/**
 * Renders the admin file detail editor and coordinates saving, moving, and deleting a file.
 */
export function EditFileSidebar({
  fileDetail,
  onFileRemoved,
  onFileUpdated,
}: AdminFileDetailPanelProps) {
  const { t } = useTranslation();
  const fileId = fileDetail.file.id;

  const {
    metadataState,
    metadataValueState,
    setMetadata,
    doSave: saveMetadata,
    resetWith: resetMetadataWith,
  } = useDocumentMetadataState(buildInitialMetadataState(fileDetail));

  const {
    deviceState,
    deviceValueState,
    update: updateDeviceState,
    resetWith: resetDeviceWith,
  } = useDocumentDeviceState(buildInitialDeviceState(fileDetail));

  const [isSaving, setIsSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [isMoveSubmitting, setIsMoveSubmitting] = useState(false);
  const [pendingDeleteFile, setPendingDeleteFile] = useState(false);
  const [isDeletingFile, setIsDeletingFile] = useState(false);

  const { state: fileObjectsState, actions: fileObjectsActions } = useFileObjectRows({
    fileId,
    saveImmediately: true,
  }, fileDetail.file.name);

  const { data: allFileDetail } = useDocumentFile(fileId, true);
  const ivalidateFileQueryBase = useInvalidateDocumentFileAll();

  useEffect(() => {
    if (!allFileDetail) return;

    resetMetadataWith({
      name: allFileDetail.file.name,
      selectedFolder: allFileDetail.folderId,
      selectedCompanyLevel: allFileDetail.companyAccess?.companyLevel
    });

    resetDeviceWith(allFileDetail.deviceAccess[0] ?? null);
    fileObjectsActions.reset(buildInitialFileObjectRows(allFileDetail));
  }, [allFileDetail, resetMetadataWith, resetDeviceWith, fileObjectsActions]);

  const invalidateFileQuery = useCallback(() => {
    ivalidateFileQueryBase(fileId);
  }, [ivalidateFileQueryBase, fileId]);

  const hasValidationErrors = metadataValueState.hasErrors || deviceValueState.hasError;
  const hasFormChanges = metadataValueState.hasChanges || deviceValueState.hasChanges;
  // Include fileObjectsState.isDirty so a language change also enables the Save button.
  const isSaveEnabled = (hasFormChanges || fileObjectsState.isDirty) && !hasValidationErrors;

  const sectionActions = useMemo(
    () => createFileObjectsSectionActions(fileObjectsActions),
    [fileObjectsActions]
  );
  const firstRow = fileObjectsState.rows[0];

  const handleSaveAll = useCallback(async () => {
    if (hasValidationErrors) {
      setShowErrors(true);
      return;
    }

    setIsSaving(true);
    try {
      const savedMetadataState = await saveMetadata(fileId);
      onFileUpdated(
        fileId,
        savedMetadataState.name.trim(),
        Number(savedMetadataState.category)
      );

      if (deviceState) {
        await addDocumentFileDeviceAccess(fileId, {
          deviceModelId: deviceState.deviceModelId,
          userLevel: deviceState.userLevel,
        });
      } else {
        await removeDocumentFileDeviceAccess(fileId, { deviceModelId: 0 });
      }

      // Persist language changes on the single file object.
      await fileObjectsActions.save();

      invalidateFileQuery();
      toast.success(t('documents.detail.saveMetadataSuccess'));
    } catch (error) {
      toast.error(
        getDocumentMutationErrorMessage(error, t) || t('documents.detail.saveMetadataError')
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    hasValidationErrors,
    deviceState,
    fileId,
    fileObjectsActions,
    invalidateFileQuery,
    onFileUpdated,
    saveMetadata,
    t,
  ]);

  const handleMoveSelect = useCallback(
    async (targetFolder: FolderSelection) => {
      setIsMoveSubmitting(true);
      try {
        await moveDocumentFile(fileId, { newFolderId: targetFolder.id });
        setIsMoving(false);
        onFileRemoved(fileId);
      } catch (error) {
        toast.error(
          getDocumentMutationErrorMessage(error, t) || t('documents.detail.moveError')
        );
      } finally {
        setIsMoveSubmitting(false);
      }
    },
    [fileId, onFileRemoved, t]
  );

  const handleDeleteFile = useCallback(async () => {
    setIsDeletingFile(true);
    try {
      await deleteDocumentFile(fileId);
      setPendingDeleteFile(false);
      onFileRemoved(fileId);
    } catch (error) {
      toast.error(
        getDocumentMutationErrorMessage(error, t) || t('documents.detail.deleteError')
      );
    } finally {
      setIsDeletingFile(false);
    }
  }, [fileId, onFileRemoved, t]);

  return (<>
    <SidebarContent>
      <DocumentMetadataSection
        state={metadataState}
        isLocked={isSaving}
        initialFolder={null}
        hideFolderSelection
        showErrors={showErrors}
        onStateChange={setMetadata}
        langauageSetting={{
          firstRow: firstRow,
          isLocked: isSaving,
          sectionActions: sectionActions,
        }}
      />

      {firstRow ? (
        <SingleFileObject
          row={firstRow}
          isLocked={isSaving}
          mode="detail"
          onFileSelect={sectionActions.onFileSelect}
          onValidationError={sectionActions.onValidationError}
          onOpen={sectionActions.onOpen}
          onUpload={sectionActions.onUploadFile}
        />
      ) : null}

      <DocumentDeviceAssignmentSection
        state={deviceState}
        isLocked={isSaving}
        showErrors={showErrors}
        onStateChange={updateDeviceState}
      />

      <DocumentShareSection
        fileId={fileId}
        initialShareLink={fileDetail.shareLink}
      />

      <div className="h-[100px]" />
    </SidebarContent>

    <SidebarFooter>
      <button
        type="button"
        onClick={() => setIsMoving(true)}
        disabled={isSaving}
        className="border-border bg-surface text-onsurface hover:bg-surface-darker/40 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50"
      >
        {t('documents.detail.moveFile')}
      </button>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPendingDeleteFile(true)}
          disabled={isSaving}
          className="border-error/30 text-error hover:bg-error/5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50"
        >
          {t('documents.detail.deleteFile')}
        </button>

        <PrimaryButton
          size="sm"
          onClick={() => void handleSaveAll()}
          isLoading={isSaving}
          disabled={!isSaveEnabled}
        >
          {t('documents.detail.saveMetadata')}
        </PrimaryButton>
      </div>
    </SidebarFooter>

    <ConfirmModal
      isOpen={pendingDeleteFile}
      title={t('documents.detail.deleteFileConfirmTitle', { name: fileDetail.file.name })}
      message={t('documents.detail.deleteFileConfirmMessage')}
      confirmLabel={t('documents.detail.deleteFile')}
      cancelLabel={t('common.cancel', { defaultValue: 'Cancel' })}
      variant="danger"
      isLoading={isDeletingFile}
      onConfirm={() => { handleDeleteFile(); }}
      onCancel={() => setPendingDeleteFile(false)}
    />

    <FolderPickerDialog
      open={isMoving}
      initialFolderId={fileDetail.file.folderId ?? undefined}
      isSubmitting={isMoveSubmitting}
      onSelect={(folder) => void handleMoveSelect(folder)}
      onCancel={() => setIsMoving(false)}
    />
  </>
  );
}

// Builds initial metadata state from the current file details payload.
function buildInitialMetadataState(fileDetail: FileDetail): DocumentMetadataState {
  return {
    name: fileDetail.file.name,
    selectedFolder: null,
    category: String(fileDetail.file.category),
    selectedCompanyLevel: undefined,
  };
}

// Extracts the first device assignment to seed the device section state.
function buildInitialDeviceState(fileDetail: FileDetail): DocumentDeviceAssignmentState | undefined {
  const firstDeviceAccess = fileDetail.deviceAccess[0];
  if (!firstDeviceAccess) return undefined;

  return {
    deviceModelId: firstDeviceAccess.deviceModelId,
    userLevel: firstDeviceAccess.userLevel,
  };
}

// Maps API file objects into editable file object rows for the form section.
function buildInitialFileObjectRows(fileDetail: FileDetail): InitialFileObjectRow[] {
  return fileDetail.fileObjects.map((fileObject) => ({
    values: {
      ...fileObject,
      objectId: fileObject.id,
    },
    file: null,
    changeState: 'unchanged',
  }));
}

interface AdminFileDetailPanelProps {
  fileDetail: FileDetail;
  onFileRemoved: (fileId: string) => void;
  onFileUpdated: (fileId: string, name: string, category: number) => void;
}

interface FolderSelection {
  id: string | null;
  name: string;
}
